import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Chip from '@mui/material/Chip';
import TableRowsIcon from '@mui/icons-material/TableRows';
import GridViewIcon from '@mui/icons-material/GridView';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getUsers, banUser, unbanUser, deleteUser } from '../api/users';
import { getRoles } from '../api/roles';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { useFilters } from '../context/FilterContext';
import {
  SearchInput,
  FilterChips,
  SortableTableHead,
  ConfirmationDialog,
  AvatarWithFallback,
} from '../components';
import { Card, ToggleButtonGroup, ToggleButton, Button } from '../components/ui-kit';
import {
  USER_STATUS_OPTIONS,
  PROVIDER_OPTIONS,
  PROVIDER_LABELS,
  FILTER_CONFIG
} from '../utils/filterConstants';
import { getAvatarUrl } from '../utils/avatarHelpers';

// Column definitions for the user table
const USER_TABLE_COLUMNS = [
  { 
    field: 'avatar', 
    headerName: '', 
    sortable: false, 
    width: '60px',
    minWidth: 60
  },
  { 
    field: 'fullName', 
    headerName: 'Full Name', 
    sortable: true,
    minWidth: 150,
    width: '15%'
  },
  { 
    field: 'email', 
    headerName: 'Email', 
    sortable: true,
    minWidth: 180,
    width: '20%'
  },
  { 
    field: 'roles', 
    headerName: 'Roles', 
    sortable: false,
    minWidth: 120,
    width: '12%'
  },
  { 
    field: 'providers', 
    headerName: 'Auth Providers', 
    sortable: false,
    minWidth: 150,
    width: '15%'
  },
  {
    field: 'isActive',
    headerName: 'Status',
    sortable: false,
    align: 'center',
    minWidth: 100,
    width: '10%'
  },
  {
    field: 'isEmailVerified',
    headerName: 'Email',
    sortable: false,
    align: 'center',
    minWidth: 100,
    width: '10%'
  },
  {
    field: 'createdAt',
    headerName: 'Created', 
    sortable: true,
    minWidth: 110,
    width: '12%'
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    align: 'left',
    width: '100px',
    minWidth: 100,
  },
];


// Компонент для выбора статуса
const StatusFilter = React.memo(({ value, onChange }) => (
  <Box>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Status
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {USER_STATUS_OPTIONS.map((option) => (
        <Button
          key={option.value}
          variant={
            (option.value === 'all' && value === null) ||
            (option.value === 'active' && value === true) ||
            (option.value === 'inactive' && value === false)
              ? 'contained' : 'outlined'
          }
          size="small"
          onClick={() => {
            const newValue = option.value === 'all' ? null : option.value === 'active';
            onChange(newValue);
          }}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  </Box>
));

// Компонент для выбора дат
const DateRangeFilter = React.memo(({ startDate, endDate, onChange, onClear }) => {
  // Локальное состояние для дат с debounce
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);

  // Синхронизация с внешним состоянием
  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  // Debounced обновление
  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localStartDate, localEndDate);
    }, FILTER_CONFIG.DATE_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localStartDate, localEndDate, onChange]);

  const handleStartDateChange = useCallback((newValue) => {
    setLocalStartDate(newValue);
  }, []);

  const handleEndDateChange = useCallback((newValue) => {
    setLocalEndDate(newValue);
  }, []);

  const handleClear = useCallback(() => {
    setLocalStartDate(null);
    setLocalEndDate(null);
    onClear();
  }, [onClear]);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Creation Date
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DatePicker
            label="From"
            value={localStartDate}
            onChange={handleStartDateChange}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiInputBase-root': {
                    height: FILTER_CONFIG.INPUT_HEIGHT,
                  },
                }
              }
            }}
          />
          <DatePicker
            label="To"
            value={localEndDate}
            onChange={handleEndDateChange}
            slotProps={{
              textField: {
                size: 'small',
                sx: {
                  '& .MuiInputBase-root': {
                    height: FILTER_CONFIG.INPUT_HEIGHT,
                  },
                }
              }
            }}
          />
          {(localStartDate || localEndDate) && (
            <Button
              size="small"
              onClick={handleClear}
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
            >
              Clear dates
            </Button>
          )}
        </Box>
      </LocalizationProvider>
    </Box>
  );
});

// Общий компонент для мультиселектов
const MultiSelectFilter = React.memo(({
  label,
  options,
  value,
  onChange,
  loading = false,
  error = false,
  placeholder = "Select options"
}) => {
  // Нормализация опций - приводим к единому формату
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map(option => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return option;
    });
  }, [options]);

  // Мемоизация выбранных опций
  const selectedOptions = useMemo(() => {
    if (!value || !Array.isArray(value)) return [];
    return normalizedOptions.filter(option => value.includes(option.value));
  }, [normalizedOptions, value]);

  const handleChange = useCallback((event, newValue) => {
    const newValues = newValue.map(item => item.value);
    onChange(newValues);
  }, [onChange]);

  if (loading) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Loading {label.toLowerCase()}...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Failed to load {label.toLowerCase()}
        </Typography>
      </Box>
    );
  }

  if (normalizedOptions.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No {label.toLowerCase()} available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {label}
      </Typography>
      <Autocomplete
        multiple
        options={normalizedOptions}
        value={selectedOptions}
        onChange={handleChange}
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={value?.length > 0 ? "" : placeholder}
            size="small"
            sx={{
              '& .MuiInputBase-root': {
                minHeight: '40px',
                height: 'auto',
                alignItems: 'flex-start',
                paddingTop: '6px',
                paddingBottom: '6px',
              },
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              variant="outlined"
              label={option.label}
              size="small"
              {...getTagProps({ index })}
              key={option.value}
            />
          ))
        }
        size="small"
        sx={{ minWidth: FILTER_CONFIG.MULTISELECT_MIN_WIDTH }}
      />
    </Box>
  );
});

// Основной компонент фильтров
const UserFilterPanel = React.memo(({
  roleOptions = [],
  isRolesLoading = false,
  isRolesError = false,
  onFiltersChange,
  isExpanded = false,
  onToggleExpand = () => {}
}) => {
  const { filters, setFilter, resetFilters, setDateRange } = onFiltersChange;

  // Мемоизация обработчиков для предотвращения лишних ререндеров
  const handleStatusChange = useCallback((value) => {
    setFilter('isActive', value);
  }, [setFilter]);

  const handleRolesChange = useCallback((roles) => {
    setFilter('roles', roles);
  }, [setFilter]);

  const handleProvidersChange = useCallback((providers) => {
    setFilter('providers', providers);
  }, [setFilter]);

  const handleDateRangeChange = useCallback((start, end) => {
    const startISO = start ? start.toISOString() : null;
    const endISO = end ? end.toISOString() : null;
    setDateRange(startISO, endISO);
  }, [setDateRange]);

  const handleDateRangeClear = useCallback(() => {
    setDateRange(null, null);
  }, [setDateRange]);

  const handleClearAllFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleToggleExpand = useCallback(() => {
    onToggleExpand();
  }, [onToggleExpand]);

  // Мемоизация значений дат
  const startDate = useMemo(() => {
    return filters.dateRange?.start ? dayjs(filters.dateRange.start) : null;
  }, [filters.dateRange?.start]);

  const endDate = useMemo(() => {
    return filters.dateRange?.end ? dayjs(filters.dateRange.end) : null;
  }, [filters.dateRange?.end]);

  return (
    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
      <Box
        sx={{
          mt: 2,
          p: 3,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500, mb: 1 }}>
          Filter Users
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatusFilter
              value={filters.isActive}
              onChange={handleStatusChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              onClear={handleDateRangeClear}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MultiSelectFilter
              label="Roles"
              options={roleOptions}
              value={filters.roles}
              onChange={handleRolesChange}
              loading={isRolesLoading}
              error={isRolesError}
              placeholder="Select roles"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MultiSelectFilter
              label="Providers"
              options={PROVIDER_OPTIONS}
              value={filters.providers}
              onChange={handleProvidersChange}
              placeholder="Select providers"
            />
          </Grid>
        </Grid>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          pt: 2, 
          borderTop: 1, 
          borderColor: 'divider' 
        }}>
          <Button
            startIcon={<RestartAltIcon />}
            onClick={handleClearAllFilters}
            variant="neutral"
            size="small"
          >
            Clear All Filters
          </Button>
          <Button onClick={handleToggleExpand} variant="neutral" size="small">
            Close
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
});

// Calculate active filter count
export const getActiveFilterCount = (filters) => [
  filters.isActive !== null ? 1 : 0,
  filters.roles?.length > 0 ? 1 : 0,
  filters.providers?.length > 0 ? 1 : 0,
  filters.dateRange?.start || filters.dateRange?.end ? 1 : 0
].reduce((a, b) => a + b, 0);

function UsersList() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const { filters, setFilter, resetFilters, setDateRange } = useFilters();

  const hasAnyUserAction = useMemo(() =>
    hasPermission('auth-service', 'user', 'update') ||
    hasPermission('auth-service', 'user', 'activate') ||
    hasPermission('auth-service', 'user', 'deactivate') ||
    hasPermission('auth-service', 'user', 'delete'),
  [hasPermission]);

  // UI state
  const [viewMode, setViewMode] = useState(() => {
    return window.innerWidth < 768 ? 'card' : 'table';
  });
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [unbanDialogOpen, setUnbanDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Fix three-state sorting toggle logic
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSortRequest = useCallback((field) => {
    if (field === 'createdAt') {
      // Only toggle between asc and desc for createdAt
      if (sortField === 'createdAt') {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField('createdAt');
        setSortDirection('desc'); // Default to newest first
      }
    } else {
      // Three-state toggle for other columns
      if (sortField === field) {
        if (sortDirection === 'asc') setSortDirection('desc');
        else if (sortDirection === 'desc') {
          setSortField('createdAt');
          setSortDirection('desc');
        } else setSortDirection('asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    }
  }, [sortField, sortDirection]);

  // Update view mode when window is resized
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      if (mobile) {
        setViewMode('card');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clear all filters on unmount
  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [resetFilters]);

  // Add useSearchParams to ensure filter state is initialized from URL
  const [searchParams] = useSearchParams();

  // On mount, ensure filter state is synced with URL params (if not already handled by FilterContext)
  useEffect(() => {
    // This effect is a safeguard in case FilterContext does not sync on mount
    // If FilterContext already does this, this effect will be a no-op
    const urlIsActive = searchParams.get('isActive');
    let isActive = null;
    if (urlIsActive === 'true') isActive = true;
    else if (urlIsActive === 'false') isActive = false;
    if (filters.isActive !== isActive) {
      setFilter('isActive', isActive);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Ensure useQuery always uses sortField/sortDirection for API calls
  const { data, isLoading, isError, error, refetch } = useQuery(
    ['users', filters, { field: sortField, direction: sortDirection }],
    () => getUsers(
      filters.page,
      filters.limit,
      filters.search,
      {
        isActive: filters.isActive,
        roles: filters.roles,
        providers: filters.providers,
        dateRange: filters.dateRange,
      },
      {
        field: sortField,
        direction: sortDirection
      }
    ),
    {
      keepPreviousData: true,
    }
  );

  // Fetch all roles for filter options - load automatically on mount
  const {
    data: rolesData,
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useQuery(
    ['roles'],
    () => getRoles({ isCommon: '1' }),
    { retry: 2 }
  );

  const roleOptions = useMemo(() => {
    if (!rolesData?.roles || !Array.isArray(rolesData.roles)) return [];
    return rolesData.roles.map(role => ({
      value: role.name,
      label: role.name.charAt(0).toUpperCase() + role.name.slice(1)
    }));
  }, [rolesData?.roles]);


  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setFilter('page', newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setFilter('limit', parseInt(event.target.value, 10));
    setFilter('page', 1);
  };

  // View mode handler
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Search handler
  const handleSearch = useCallback((value) => {
    setFilter('search', value);
  }, [setFilter]);

  // Navigate to edit user
  const handleEditUser = (userId) => {
    navigate(`/users/${userId}`);
  };

  // Open ban user dialog
  const handleOpenBanDialog = (user) => {
    setSelectedUser(user);
    setBanReason('');
    setBanDialogOpen(true);
  };

  // Close ban user dialog
  const handleCloseBanDialog = () => {
    setBanDialogOpen(false);
    setSelectedUser(null);
  };

  // Open unban user dialog
  const handleOpenUnbanDialog = (user) => {
    setSelectedUser(user);
    setUnbanDialogOpen(true);
  };

  // Close unban user dialog
  const handleCloseUnbanDialog = () => {
    setUnbanDialogOpen(false);
    setSelectedUser(null);
  };

  // Ban user
  const handleBanUser = async () => {
    if (!selectedUser) return;

    try {
      await banUser(selectedUser.id, banReason);
      toast.success('User has been banned');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error banning user');
    } finally {
      handleCloseBanDialog();
    }
  };

  // Unban user
  const handleUnbanUser = async () => {
    if (!selectedUser) return;

    try {
      await unbanUser(selectedUser.id);
      toast.success('User has been unbanned');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error unbanning user');
    } finally {
      handleCloseUnbanDialog();
    }
  };

  // Handler to open delete dialog
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };
  // Handler to close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };
  // Handler to confirm delete
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      toast.success('User deleted successfully');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting user');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  // User Card Component
  const UserCard = ({ user }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: '12px',
        background: (theme) =>
          `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        color: 'text.primary',
        overflow: 'hidden',
        position: 'relative',
        p: 2, // reduced from 2.5
        '&:hover': {
          boxShadow: (theme) => `0 12px 24px ${theme.palette.common.black}30`,
        },
      }}
    >
      {/* Header Row - Avatar, Name, Email and Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          mb: 2,
          gap: 1,
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flex: '1 1 200px', minWidth: 0, alignItems: 'center', overflow: 'hidden' }}>
          <AvatarWithFallback
            src={getAvatarUrl(user.avatar)}
            alt={user.fullName}
            size={36}
            sx={{
              flexShrink: 0,
              backgroundColor: 'action.selected',
              color: 'text.secondary',
            }}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.2,
                fontSize: '1rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.fullName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center', ml: 'auto' }}>
          {hasPermission('auth-service', 'user', 'update') && (
            <IconButton
              aria-label="edit"
              onClick={() => handleEditUser(user.id)}
              sx={{
                width: 28,
                height: 28,
                padding: 0,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'action.hover',
                },
                transition: 'all 0.2s',
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}

          {user.isActive
            ? hasPermission('auth-service', 'user', 'deactivate') && (
                <IconButton
                  aria-label="ban"
                  onClick={() => handleOpenBanDialog(user)}
                  sx={{
                    width: 28,
                    height: 28,
                    padding: 0,
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.error.light + '20',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              )
            : hasPermission('auth-service', 'user', 'activate') && (
                <IconButton
                  aria-label="unban"
                  onClick={() => handleOpenUnbanDialog(user)}
                  sx={{
                    width: 28,
                    height: 28,
                    padding: 0,
                    color: 'success.main',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.success.light + '20',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              )}

          {hasPermission('auth-service', 'user', 'delete') && user.id !== (currentUser && currentUser.id) && (
            <IconButton
              aria-label="delete"
              onClick={() => handleOpenDeleteDialog(user)}
              color="error"
              sx={{
                width: 28,
                height: 28,
                padding: 0,
                ml: 0.5,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.error.light + '20',
                },
                transition: 'all 0.2s',
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Info Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Role Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            Role:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {user.roles && user.roles.length > 0 ? (
              user.roles.map((role, index) => (
                <Chip
                  key={role.id || role.name || index}
                  label={role}
                  size="small"
                  sx={{
                    backgroundColor: (theme) =>
                      role.name === 'super-admin'
                        ? 'primary.dark'
                        : 'action.selected',
                    color: 'text.primary',
                    height: '20px',
                    fontSize: '0.7rem',
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              ))
            ) : (
              <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                No roles
              </Typography>
            )}
          </Box>
        </Box>

        {/* Status Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            Status:
          </Typography>
          <Typography
            sx={{
              color: user.isActive ? 'success.main' : 'error.main',
              fontWeight: user.isActive ? 400 : 500,
              fontSize: '0.8rem',
            }}
          >
            {user.isActive ? 'Active' : 'Banned'}
          </Typography>
        </Box>

        {/* Email Verified Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            Email:
          </Typography>
          <Typography
            sx={{
              color: user.isEmailVerified ? 'success.main' : 'warning.main',
              fontWeight: user.isEmailVerified ? 400 : 500,
              fontSize: '0.8rem',
            }}
          >
            {user.isEmailVerified ? 'Verified' : 'Not verified'}
          </Typography>
        </Box>

        {/* Id Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            ID:
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 400,
              fontSize: '0.8rem',
            }}
          >
            {user.id}
          </Typography>
        </Box>
      </Box>
    </Card>
  );

  return (
    <Box sx={{ pb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Users
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="table" aria-label="table view">
              <TableRowsIcon />
            </ToggleButton>
            <ToggleButton value="card" aria-label="card view">
              <GridViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Search and filter section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <SearchInput
              placeholder="Search users by name or email..."
              onSearch={handleSearch}
              defaultValue={filters.search}
              fullWidth
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          {/* Filter icon positioned to the right of search */}
          <Tooltip title="Filter users">
            <IconButton
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
              color={getActiveFilterCount(filters) > 0 ? "primary" : "default"}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                boxShadow: getActiveFilterCount(filters) > 0 ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                '&:hover': {
                  backgroundColor: getActiveFilterCount(filters) > 0
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <Badge badgeContent={getActiveFilterCount(filters)} color="primary">
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Expandable Filter panel */}
        <UserFilterPanel
          roleOptions={roleOptions}
          isRolesLoading={isRolesLoading}
          isRolesError={isRolesError}
          onFiltersChange={{
            filters,
            setFilter,
            resetFilters,
            setDateRange
          }}
          isExpanded={isFiltersExpanded}
          onToggleExpand={() => setIsFiltersExpanded(!isFiltersExpanded)}
        />
      </Box>
      
      {/* Filter chips */}
      <FilterChips 
        roleLabels={roleOptions.reduce((acc, role) => ({ ...acc, [role.value]: role.label }), {})}
        providerLabels={PROVIDER_LABELS}
      />
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ my: 2 }}>
          Error loading users: {error?.message || 'Unknown error'}
        </Typography>
      ) : (
        <>
          {viewMode === 'table' ? (
            <>
              <TableContainer 
                sx={{ 
                  overflowX: 'auto', // Ensures horizontal scrolling
                  boxShadow: (theme) => theme.shadows[1],
                  borderRadius: 1,
                  mb: 2
                }}
              >
                <Table
                  sx={{
                    minWidth: 900,
                    width: '100%'
                  }} 
                  aria-label="users table"
                >
                  <SortableTableHead
                    columns={hasAnyUserAction ? USER_TABLE_COLUMNS : USER_TABLE_COLUMNS.filter(c => c.field !== 'actions')}
                    sortConfig={{ field: sortField, direction: sortDirection }}
                    handleSortRequest={handleSortRequest}
                  />
                  <TableBody>
                    {data?.users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          backgroundColor: user.isActive
                            ? 'inherit'
                            : 'action.disabledBackground',
                        }}
                      >
                        <TableCell sx={{ width: '60px', padding: '8px 16px' }}>
                          <AvatarWithFallback
                            src={getAvatarUrl(user.avatar)}
                            alt={user.fullName}
                            size={40}
                          />
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {user.fullName}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {user.email}
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            {user.roles?.map((role, index) => (
                              <Chip
                                key={role.id || role.name || index}
                                label={role.name || role}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            {user.providers?.map((provider) => (
                              <Chip
                                key={provider}
                                label={PROVIDER_LABELS[provider] || provider}
                                size="small"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ padding: '8px 16px' }}>
                          {user.isActive ? (
                            <Chip
                              label="Active"
                              size="small"
                              color="success"
                              sx={{ minWidth: 75 }}
                            />
                          ) : (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="error"
                              sx={{ minWidth: 75 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ padding: '8px 16px' }}>
                          <Tooltip title={user.isEmailVerified ? 'Email confirmed' : 'Email not confirmed — local password login is blocked'}>
                            <Chip
                              label={user.isEmailVerified ? 'Verified' : 'Not verified'}
                              size="small"
                              color={user.isEmailVerified ? 'success' : 'warning'}
                              variant={user.isEmailVerified ? 'filled' : 'outlined'}
                              sx={{ minWidth: 90 }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {new Date(user.metadata?.createdAt).toLocaleDateString()}
                        </TableCell>
                        {hasAnyUserAction && (
                          <TableCell align="left">
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {hasPermission('auth-service', 'user', 'update') && (
                                <IconButton
                                  aria-label="edit user"
                                  onClick={() => handleEditUser(user.id)}
                                  size="small"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {user.isActive
                                ? hasPermission('auth-service', 'user', 'deactivate') && (
                                    <IconButton
                                      aria-label="ban user"
                                      onClick={() => handleOpenBanDialog(user)}
                                      size="small"
                                      color="error"
                                    >
                                      <BlockIcon fontSize="small" />
                                    </IconButton>
                                  )
                                : hasPermission('auth-service', 'user', 'activate') && (
                                    <IconButton
                                      aria-label="unban user"
                                      onClick={() => handleOpenUnbanDialog(user)}
                                      size="small"
                                      color="success"
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  )}
                              {hasPermission('auth-service', 'user', 'delete') && user.id !== (currentUser && currentUser.id) && (
                                <IconButton onClick={() => handleOpenDeleteDialog(user)} color="error">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    
                    {data?.users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                          <Typography variant="body1">
                            No users found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
                
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={data?.pagination?.total || 0}
                rowsPerPage={filters.limit}
                page={filters.page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <>
              {/* Card view */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                  mb: 2,
                  mt: 2 // Added margin top for better spacing
                }}
              >
                {data?.users.map((user) => (
                  <UserCard key={user.id} user={user} />
                ))}
                
                {data?.users.length === 0 && (
                  <Box sx={{ gridColumn: '1 / -1', py: 4, textAlign: 'center' }}>
                    <Typography variant="body1">
                      No users found
                    </Typography>
                  </Box>
                )}
              </Box>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={data?.pagination?.total || 0}
                rowsPerPage={filters.limit}
                page={filters.page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </>
      )}
      
      {/* Ban User Dialog */}
      <ConfirmationDialog
        open={banDialogOpen}
        title="Ban User"
        contentText={selectedUser ? `Are you sure you want to ban ${selectedUser.fullName}?` : ''}
        showTextField={true}
        textFieldLabel="Ban Reason (Optional)"
        textFieldPlaceholder="Ban Reason (Optional)"
        textFieldValue={banReason}
        onTextFieldChange={(e) => setBanReason(e.target.value)}
        confirmButtonText="Ban"
        cancelButtonText="Cancel"
        onConfirm={handleBanUser}
        onClose={handleCloseBanDialog}
        confirmButtonColor="primary"
      />

      {/* Unban User Dialog */}
      <ConfirmationDialog
        open={unbanDialogOpen}
        title="Unban User"
        contentText={selectedUser ? `Are you sure you want to unban ${selectedUser.fullName}?` : ''}
        confirmButtonText="Unban"
        cancelButtonText="Cancel"
        onConfirm={handleUnbanUser}
        onClose={handleCloseUnbanDialog}
        confirmButtonColor="primary"
      />

      {/* Delete User Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        contentText={userToDelete ? `Are you sure you want to delete user ${userToDelete.fullName}? This action cannot be undone.` : ''}
        confirmButtonText="Delete"
        confirmButtonColor="error"
      />
    </Box>
  );
}

export default UsersList;
